// src/utils/otel-config.js
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter, AggregationTemporalityPreference } from '@opentelemetry/exporter-metrics-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { trace, metrics, context } from '@opentelemetry/api';
import { ConsoleSpanExporter, BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import CryptoJS from 'crypto-js';

// ========== 基础配置（适配 .NET Core 自定义路径） ==========
// const isProd = process.env.NODE_ENV === 'production';
let isProd = true;
const OTEL_HOST = window.mainbodyConfig?.WS_API?.replace(/^\/+|\/+$/g, '') || '';
console.log("protocol", window.location.protocol, "OTEL:", OTEL_HOST);
// Trace 上报路径（.NET Core 转发路径）
const OTLP_TRACE_ENDPOINT = `${window.location.protocol}//${OTEL_HOST}/v1/traces`;
// Metrics 上报路径（.NET Core 转发路径）
const OTLP_METRIC_ENDPOINT = `${window.location.protocol}//${OTEL_HOST}/v1/metrics`;

if (!OTEL_HOST) {
    console.warn("没有读取到配置，关闭log！");
    isProd = false
}else{
    console.log("上行host", OTEL_HOST);
}
const METRIC_CONFIG = {
    buckets: {
        apiRequest: [5, 10, 20, 50, 100, 200, 300, 400, 500, 750, 1000, 1500, 2000, 5000], // 接口耗时Bucket（ms）
        pageLoad: [10, 30, 50, 100, 200, 300, 500] // 页面切换耗时Bucket（ms）
    },
    sampleRate: {
        default: 1.0, // 默认全量采集
        highFreqApi: 0.2 // 高频接口（如直播心跳）采样率
    },
    threshold: {
        min: 0, // 最小耗时阈值
        maxApi: 10000,
        maxPage: 1000
    }
};
// 批量/间隔配置（核心：控制上报频率和批量大小）
const BATCH_CONFIG = {
    // Trace 批量上报配置
    trace: {
        maxQueueSize: 2000,        // 增大缓存队列（适配30秒上报，避免满队列）
        scheduledDelayMillis: 5000, // 
        exportTimeoutMillis: 5000,  // 导出超时
        maxExportBatchSize: 1000,    // 单次最多上报1000条（匹配队列大小）
        maxPage: 1000,
    },
    // Metrics 上报间隔配置
    metric: {
        exportIntervalMillis: 5000, // 导出间隔
        exportTimeoutMillis: 3000,  // 导出超时
    }
};

// 鉴权头（传给 .NET Core 做鉴权）
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('trace-token') || ''}`,
    'X-App-Id': 'vue3-client',
});

// 初始化 Trace 导出器
const traceExporter = new OTLPTraceExporter({
    url: OTLP_TRACE_ENDPOINT,
    headers: getAuthHeaders(),
    timeoutMillis: 5000,
});
const consoleExporter = new ConsoleSpanExporter();

// 初始化 Metrics 导出器
const metricExporter = new OTLPMetricExporter({
    url: OTLP_METRIC_ENDPOINT,
    headers: getAuthHeaders(),
    timeoutMillis: BATCH_CONFIG.metric.exportIntervalMillis,
    temporalityPreference: AggregationTemporalityPreference.DELTA
});

// 开发环境控制
if (!isProd) {
    traceExporter.export = (spans, cb) => cb({ code: 0 });
    metricExporter.export = (metrics, cb) => cb({ code: 0 });
}


let _clientUniqueId = null;
const getClientUniqueId = () => {
    if (_clientUniqueId) return _clientUniqueId;

    // --------------- 1. 硬件级真随机数（核心唯一） ---------------
    const randomBytes = new Uint32Array(4);
    crypto.getRandomValues(randomBytes);
    const trueRandom = randomBytes.join('-'); // 硬件随机，多窗口绝对不同

    // --------------- 2. 窗口上下文唯一标识（区分窗口） ---------------
    const windowContext = [
        window.screenX || window.screenLeft, // 窗口X坐标
        window.screenY || window.screenTop, // 窗口Y坐标
        window.innerWidth,
        window.innerHeight,
        Math.random() * window.innerWidth, // 鼠标X（随机）
        Math.random() * window.innerHeight // 鼠标Y（随机）
    ].join('|');

    // --------------- 3. 高精度时间戳（杜绝同时触发） ---------------
    const preciseTime = [
        Date.now(), // 毫秒
        performance.now(), // 微秒
        typeof process !== 'undefined' ? process.hrtime.bigint() : Math.random() * 1e9 // 纳秒/兜底
    ].join('|');

    // --------------- 4. 设备指纹（基础区分） ---------------
    const deviceFingerprint = [
        navigator.userAgent,
        screen.width,
        screen.height,
        navigator.language,
        navigator.hardwareConcurrency || 'unknown',
        navigator.deviceMemory || 'unknown',
        new Date().getTimezoneOffset()
    ].join('|');

    // --------------- 5. 拼接完整唯一字符串 ---------------
    const uniqueString = `${deviceFingerprint}|${trueRandom}|${windowContext}|${preciseTime}`;
    
    // --------------- 6. 用crypto-js的MD5生成唯一ID ---------------
    // MD5输出32位十六进制字符串，绝对唯一
    _clientUniqueId = CryptoJS.MD5(uniqueString).toString();

    // 调试：验证唯一性
    console.log('【窗口坐标】', window.screenX + ',' + window.screenY);
    console.log('【真随机数】', trueRandom);
    console.log('【最终唯一ID】', _clientUniqueId);

    return _clientUniqueId;
};

// ========== 初始化 Trace（原有逻辑优化） ==========
let tracer;
if (isProd) {
    const traceProvider = new WebTracerProvider({
        resource: {
            attributes: {
                'service.name': 'h5-client',
                'service.version': window.__my_res_ver || "unknow",
                'deployment.environment': isProd ? 'production' : 'development',
                'client.unique.id': getClientUniqueId(),
            },
        },
        spanProcessors: [
            new BatchSpanProcessor(consoleExporter, BATCH_CONFIG.trace),
            new BatchSpanProcessor(traceExporter, BATCH_CONFIG.trace),
        ]
    });
    traceProvider.register({ contextManager: new ZoneContextManager() });
    tracer = trace.getTracer('h5-client-tracer');

    // 注册 HTTP 自动拦截（可选）
    //   registerInstrumentations({
    //     instrumentations: [
    //       new FetchInstrumentation({
    //         propagateTraceHeaderCorsUrls: [/^https?:\/\/your-netcore-server\.com/],
    //         ignoreUrls: [/\/health/, /\/ping/],
    //       }),
    //       new XMLHttpRequestInstrumentation({
    //         propagateTraceHeaderCorsUrls: [/^https?:\/\/your-netcore-server\.com/],
    //         ignoreUrls: [/\/health/, /\/ping/],
    //       }),
    //     ],
    //   });
} else {
    tracer = {
        startSpan: (name) => ({
            setAttribute: () => { },
            recordException: () => { },
            setStatus: () => { },
            end: () => { },
            spanContext: () => ({ traceId: 'dev-trace-id', spanId: 'dev-span-id' }),
        }),
    };
}

// ========== 初始化 Metrics ==========
let meter;
if (isProd) {
    const meterProvider = new MeterProvider({
        resource: {
            attributes: {
                'service.name': 'h5-client',
                'service.version': window.__my_res_ver || "unknow",
                'deployment.environment': isProd ? 'production' : 'development',
                'client.unique.id': getClientUniqueId(),
            },
        },
        readers: [
            new PeriodicExportingMetricReader({
                exporter: metricExporter,
                exportIntervalMillis: BATCH_CONFIG.metric.exportIntervalMillis,
                exportTimeoutMillis: BATCH_CONFIG.metric.exportTimeoutMillis
            }),
        ],
    });
    metrics.setGlobalMeterProvider(meterProvider);
    meter = meterProvider.getMeter('vue3-client-metrics');
} else {
    // 开发环境空实现
    meter = {
        createCounter: () => ({ add: () => { } }),
        createGauge: () => ({ set: () => { } }),
        createHistogram: () => ({
            record: () => { },
            // 兼容封装后的record方法
            wrapRecord: () => ({ record: () => { } })
        }),
        createUpDownCounter: () => ({ add: () => { } }),
    };
}


/**
 * 通用Trace方法（贴合原型设计）
 * @param {Object | import('@opentelemetry/api').Span | null} spanCtx - 上下文：
 *                                    - response/err → 关联服务端requestID，初始化HTTP属性
 *                                    - 父Span实例 → 创建子Span（继承上下文）
 *                                    - null → 新建根Span
 * @param {string | null} [spanName] - Span名称（null/不传则自动生成）
 * @param {Function} fn - 业务回调，入参为当前Span，后续调用有：
 *              - setAttribute(_key: string, _value: unknown)
 *              - addEvent(_name: string, _attributes?: SpanAttributes)
 * @returns {Promise<any>} 业务逻辑返回值
 */
export const traceSpan = async (spanCtx, spanName, fn) => {
    // 非生产环境：直接执行业务逻辑
    if (!isProd) return await fn(null);

    // ========== 步骤1：自动生成Span名称（核心逻辑） ==========
    const getAutoSpanName = () => {
        // 场景1：spanCtx是response/err → 取请求path
        if (spanCtx && (spanCtx.config || spanCtx.response)) {
            const config = spanCtx.config || spanCtx.response.config;
            const path = config?.url?.replace(/^\/|\/$/g, '').replace(/\//g, '-') || 'unknown-api';
            const isError = !!spanCtx.message || spanCtx.response?.status >= 400;
            return isError ? `api-error-${path}` : `api-success-${path}`;
        }

        // 场景2：spanCtx是父Span → 父Span名称+子操作后缀
        if (spanCtx?.spanContext && typeof spanCtx.spanContext === 'function') {
            const parentName = spanCtx.name || 'parent-span';
            return `${parentName}-child-${Date.now().toString().slice(-4)}`;
        }

        // 场景3：spanCtx是null → 解析调用栈生成（取上层3个函数名）
        try {
            const stack = new Error().stack.split('\n').slice(3, 6); // 跳过当前函数，取上层3个
            const funcNames = stack.map(line => {
                const match = line.match(/at\s+(.+?)\s+\(/);
                return match ? match[1].replace(/^Object\./, '').replace(/^bound /, '') : 'anonymous';
            }).filter(Boolean);
            return funcNames.join('.') || 'root-span';
        } catch (e) {
            return `root-span-${Date.now().toString().slice(-6)}`;
        }
    };

    // 最终Span名称：显式传入 > 自动生成
    const finalSpanName = spanName || getAutoSpanName();

    // ========== 步骤2：初始化Span基础信息 ==========
    let parentSpan = null;
    const baseAttributes = {
        'app.version': window.__my_res_ver,
        'device.type': /Mobile/.test(navigator.userAgent) ? 'mobile' : 'pc',
        'trace.auto_generated': spanName ? 'false' : 'true', // 标记是否自动生成名称
    };

    // 场景1：spanCtx是父Span → 记录父Span
    if (spanCtx?.spanContext && typeof spanCtx.spanContext === 'function') {
        parentSpan = spanCtx;
    }

    // 场景2：spanCtx是response/err → 初始化HTTP/requestID属性
    if (spanCtx && (spanCtx.config || spanCtx.response)) {
        const config = spanCtx.config || spanCtx.response.config;
        const responseData = spanCtx.data || spanCtx.response?.data;
        Object.assign(baseAttributes, {
            'http.method': config?.method || 'unknown',
            'http.url': config?.url || 'unknown',
            'http.status_code': spanCtx.status || spanCtx.response?.status || 0,
            'http.duration': config?._startTime ? performance.now() - config._startTime : 0,
            'trace.requestID': responseData?.requestID || 'unknown', // 关联服务端链路
        });

        // 异常场景标记基础属性
        if (spanCtx.message || spanCtx.response?.status >= 400) {
            baseAttributes['error.type'] = spanCtx.message ? 'network_error' : `http_${spanCtx.response.status}`;
            baseAttributes['error.message'] = spanCtx.message || spanCtx.data?.msg || 'unknown';
        }
    }

    // ========== 步骤3：创建Span（根/子自动区分） ==========
    let currentContext = context.active();
    if (parentSpan) {
        currentContext = trace.setSpan(currentContext, parentSpan); // 继承父Span上下文
    }
    const currentSpan = tracer.startSpan(finalSpanName, { attributes: baseAttributes }, currentContext);

    // 异常场景默认标记Span状态（response/err）
    if (spanCtx && (spanCtx.message || spanCtx.response?.status >= 400)) {
        currentSpan.setStatus({
            code: trace.SpanStatusCode.ERROR,
            message: baseAttributes['error.message'],
        });
    }

    // ========== 步骤4：执行业务逻辑 ==========
    try {
        return await context.with(trace.setSpan(currentContext, currentSpan), () => fn(currentSpan));
    } catch (businessError) {
        // 业务异常：记录并标记Span
        currentSpan.recordException(businessError);
        currentSpan.setStatus({
            code: trace.SpanStatusCode.ERROR,
            message: `业务异常：${businessError.message}`,
        });
        currentSpan.addEvent('business_error', {
            error_type: businessError.name,
            error_msg: businessError.message,
            error_time: new Date().toISOString(),
        });
        throw businessError; // 透传异常
    } finally {
        currentSpan.end(); // 结束Span（触发上报）
    }
};

export const traceRootSpan = async (spanName, fn) => await traceSpan(null, spanName, fn);

// ========== 封装常用 Metrics 指标（新增：业务层直接调用） ==========
// 8.1 计数器（Counter）：累加值（如接口请求次数、按钮点击数）
export const createCounter = (name, options = {}) => {
    return meter.createCounter(name, {
        description: options.description || '',
        unit: options.unit || 'count',
    });
};

// 8.2 仪表盘（Gauge）：瞬时值（如页面内存占用、在线用户数）
export const createGauge = (name, options = {}) => {
    return meter.createGauge(name, {
        description: options.description || '',
        unit: options.unit || '',
    });
};

// 8.3 直方图（Histogram）：分布值（如接口耗时、页面加载时间）
export const createHistogram = (name, options = {}) => {
    const histogram = meter.createHistogram(name, {
        description: options.description || '',
        unit: options.unit || 'ms',
        // valueType: ValueType.DOUBLE,
        // boundaries: options.buckets || [],
        advice: {
          explicitBucketBoundaries: options.buckets || [],
        },
    });

    const record = (value, attributes = {}, sampleRate = METRIC_CONFIG.sampleRate.default) => {
        // 1. 采样逻辑
        if (Math.random() > sampleRate) return;

        // 2. 异常值过滤
        const min = options.minThreshold || METRIC_CONFIG.threshold.min;
        const max = options.maxThreshold || Infinity;
        if (value <= min || value > max) {
            console.debug(`[Metric] 过滤异常值: ${name} = ${value}ms`, attributes);
            return;
        }

        // 3. 记录数据（Delta模式：30秒窗口内累积，上报后清零）
        histogram.record(value, attributes);
    };

    // 返回原始实例+封装后的record方法
    return {
        histogram,
        record
    };
};

// ========== 预设通用指标（开箱即用） ==========

let MetricesAllow = {
    "api.request.count": {
        "http.method": ["GET", "POST"],
        "client.version": ["v1.0.0", "v1.0.2", "v1.0.3"],
        "http.host": ["192.168.0.80", "192.168.0.236"]
    },
    "api.request.error.count": {
        "http.method": ["GET", "POST"],
        "client.version": ["v1.0.0", "v1.0.2", "v1.0.3"],
        "http.host": ["192.168.0.80", "192.168.0.236"],
        "request.status": ["200", "400", "500"]
    },
    "api.request.duration": {
        "http.method": ["GET", "POST"],
        "client.version": ["v1.0.0", "v1.0.2", "v1.0.3"],
        "http.host": ["192.168.0.80", "192.168.0.236"],
        "request.status": ["200", "400", "500"]
    },
    "page.load.duration": {
        "page": ["root"],
        "type": ["initial", "route"],
        "host": ["192.168.0.80:845"]
    }
}

// 接口请求总次数
const apiRequestCounter = createCounter('api.request.count', {
    description: 'Total number of API requests from client',
    unit: 'count',
});

/**
 * 记录接口请求总次数
 * @param {string} method HTTP方法（GET/POST等）
 * @param {string} version 客户端版本
 * @param {string} host 请求域名/IP
 * @param {object} extra 额外标签（可选）
 * @param {number} value 计数增量（默认1）
 */
export const recordApiRequestCount = (method, version, host, extra = {}, value = 1) => {
    const urlObj = new URL(host);
    var _urlHost = urlObj.origin;
    const attributes = {
        "http.method": method,
        "client.version": version,
        "http.host": _urlHost,
        ...extra,
    };
    apiRequestCounter.add(value, attributes);
};

// 接口错误次数计数器
const apiRequestErrorCounter = createCounter('api.request.error.count', {
    description: 'Number of failed API requests from client',
    unit: 'count',
});

/**
 * 记录接口错误次数
 * @param {string} method HTTP方法（GET/POST等）
 * @param {string} version 客户端版本
 * @param {string} host 请求域名/IP
 * @param {string|number} status 错误状态码（400/500等）
 * @param {object} extra 额外标签（可选）
 * @param {number} value 计数增量（默认1）
 */
export const recordApiRequestErrorCount = (method, version, host, status, extra = {}, value = 1) => {
    const urlObj = new URL(host);
    var _urlHost = urlObj.origin;
    const attributes = {
        "http.method": method,
        "client.version": version,
        "http.host": _urlHost,
        "request.status": status,
        ...extra,
    };
    apiRequestErrorCounter.add(value, attributes);
};

// 接口请求耗时分布
var apiRequestDuration = createHistogram('api.request.duration', {
    description: 'API request duration in milliseconds',
    unit: 'ms',
    buckets: METRIC_CONFIG.buckets.apiRequest, // 接口耗时Bucket
    maxThreshold: METRIC_CONFIG.threshold.maxApi // 最大阈值10s
});
/**
 * 记录接口请求耗时（带高频采样）
 * @param {number} duration 耗时(ms)
 * @param {string} apiType 接口类型（如live_heartbeat/match_detail）
 * @param {string} url 接口地址
 * @param {boolean} isHighFreq 是否高频接口（默认false）
 * @param {object} attributes 额外标签
 */
export const recordApiRequestDuration = (duration, method, version, host, status, isHighFreq = false, extra = {}) => {
    const urlObj = new URL(host);
    var _urlHost = urlObj.origin;
    const attributes = {
        "http.method": method,
        "client.version": version,
        "http.host": _urlHost,
        "request.status": status,
        ...extra,
    };
    const sampleRate = isHighFreq ? METRIC_CONFIG.sampleRate.highFreq : METRIC_CONFIG.sampleRate.default;
    apiRequestDuration.record(duration, attributes, sampleRate);
};

// 接口传输耗时分布
var apiTranDuration = createHistogram('api.tran.duration', {
    description: 'API translation duration in milliseconds',
    unit: 'ms',
    buckets: METRIC_CONFIG.buckets.apiRequest, // 接口耗时Bucket
    maxThreshold: METRIC_CONFIG.threshold.maxApi // 最大阈值10s
});
/**
 * 记录接口传输耗时（带高频采样）
 * @param {number} duration 耗时(ms)
 * @param {string} apiType 接口类型（如live_heartbeat/match_detail）
 * @param {string} url 接口地址
 * @param {boolean} isHighFreq 是否高频接口（默认false）
 * @param {object} attributes 额外标签
 */
export const recordApiTranDuration = (duration, method, version, host, status, isHighFreq = false, extra = {}) => {
    const urlObj = new URL(host);
    var _urlHost = urlObj.origin;
    const attributes = {
        "http.method": method,
        "client.version": version,
        "http.host": _urlHost,
        "request.status": status,
        ...extra,
    };
    const sampleRate = isHighFreq ? METRIC_CONFIG.sampleRate.highFreq : METRIC_CONFIG.sampleRate.default;
    apiTranDuration.record(duration, attributes, sampleRate);
};

// 页面加载耗时
const pageLoadDuration = createHistogram('page.load.duration', {
    description: 'Page load duration in milliseconds',
    unit: 'ms',
    buckets: METRIC_CONFIG.buckets.pageLoad, // 页面切换耗时Bucket
    maxThreshold: METRIC_CONFIG.threshold.maxPage // 最大阈值1s
});
/**
 * 记录页面加载耗时
 * @param {number} duration 页面加载耗时(ms)
 * @param {string} version 前端程序版本
 * @param {string} url 页面名称完整url
 * @param {string} type 加载类型（initial/route）
 * @param {object} extra 额外标签（可选）
 */
export const recordPageLoadDuration = (duration, version, url, type, extra = {}) => {
    const urlObj = new URL(url);
    var _urlHost = urlObj.origin;
    var _urlPath = urlObj.pathname;

    const attributes = {
        "page": _urlPath,
        "type": type,
        "client.version": version,
        "host": _urlHost,
        ...extra,
    };
    // 页面加载耗时无高频采样，直接记录
    pageLoadDuration.record(duration, attributes);
};

// // 测试
// setInterval(() => {
//     // api.request.count        api_request_count_total
//     recordApiRequestCount("GET", "tester", "http://192.168.0.236:10828/api/startup", {"delme":"1"});
//     recordApiRequestCount("POST", "tester", "http://192.168.0.236:12828/api/startup", {"delme":"1"});
//     recordApiRequestCount("GET1", "tester", "http://192.168.0.236:13828/api/startup", {"delme":"1"});

//     recordApiRequestErrorCount('GET', "tester", "http://192.168.0.236:10828/api/startup", 404, {"delme":"1"});

//     recordApiRequestDuration(Math.random() * 1000, "GET", "tester", "http://192.168.0.236:10828/api/startup", 200, false, {"delme":"1"});
//     recordApiRequestDuration(Math.random() * 1000, "GET1", "tester", "http://192.168.0.236:10828/api/startup", 200, false, {"delme":"1"});
    
//     recordApiTranDuration(Math.random() * 500, "GET", "tester", "http://192.168.0.236:10828/api/startup", 200, false, {"delme":"1"});

//     recordPageLoadDuration(Math.random() * 1000, "tester", "http://baidu.com", "initial", {"delme":"1"});
//     recordPageLoadDuration(Math.random() * 1000, "tester", "http://192.168.0.80:842", "initial", {"delme":"1"});


//     console.log("metric test sent");
// }, 1000);


// ========== 导出核心实例 ==========
export { tracer, meter };
